import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@oasisprotocol/ui-library/src/components/cards'
import { LinkableDiv } from '../../components/PageLayout/LinkableDiv'
import { useAccount } from './hook'
import { CardEmptyState } from '../../components/CardEmptyState'
import { TokenDashboardContext } from '../TokenDashboardPage'
import { CodeDisplay } from '../../components/CodeDisplay'
import { codeContainerId, sourceCodeContainerId } from '../../utils/tabAnchors'
import { Typography } from '@oasisprotocol/ui-library/src/components/typography'
import { VerificationIcon } from 'app/components/ContractVerificationIcon'
import { useTokenInfo } from '../TokenDashboardPage/hook'
import { base64ToHex } from '../../utils/helpers'
import { Separator } from '@oasisprotocol/ui-library/src/components/ui/separator'
import { fetchSourcifySources, SourcifySourcesResult } from '../../utils/sourcify'

export const ContractCodeCard: FC<TokenDashboardContext> = ({ scope, address }) => {
  const { t } = useTranslation()

  const { isFetched, account } = useAccount(scope, address)
  const contract = account?.evm_contract
  const noCode = isFetched && !contract?.creation_bytecode && !contract?.runtime_bytecode
  const { token } = useTokenInfo(scope, address, {
    enabled: !!contract,
  })

  const nexusVerification = contract?.verification
  const [sourcify, setSourcify] = useState<SourcifySourcesResult | undefined>()

  useEffect(() => {
    // Nexus already has Sourcify-backed verification — no extra fetch.
    if (nexusVerification?.source_files?.length || !account?.address_eth) {
      setSourcify(undefined)
      return
    }
    let cancelled = false
    fetchSourcifySources(account.address_eth)
      .then(result => {
        if (!cancelled) setSourcify(result)
      })
      .catch(() => {
        if (!cancelled) setSourcify(undefined)
      })
    return () => {
      cancelled = true
    }
  }, [account?.address_eth, nexusVerification?.source_files?.length])

  const verificationLevel = nexusVerification?.verification_level ?? sourcify?.verificationLevel
  const compilationMetadata =
    nexusVerification?.compilation_metadata ?? sourcify?.compilationMetadata
  const sourceFiles = useMemo(() => {
    if (nexusVerification?.source_files?.length) return nexusVerification.source_files
    return sourcify?.sourceFiles
  }, [nexusVerification?.source_files, sourcify?.sourceFiles])

  const entryFilePath = Object.keys(
    (compilationMetadata?.settings as { compilationTarget?: Record<string, string> } | undefined)
      ?.compilationTarget ?? {},
  )[0]
  const filesSorted = sourceFiles
    ?.slice()
    .sort((a, b) => Number(b.path.endsWith(entryFilePath)) - Number(a.path.endsWith(entryFilePath)))

  const hasSources = !!filesSorted?.length

  return (
    <Card variant="layout" className="rounded-t-none border-t-0">
      {noCode && <CardEmptyState label={t('contract.noCode')} />}
      {contract && (contract.creation_bytecode || contract.runtime_bytecode) && (
        <CardContent>
          <LinkableDiv id={codeContainerId}>
            {hasSources && (
              <LinkableDiv id={sourceCodeContainerId}>
                <Typography variant="h4" className="mb-4">
                  {t('contract.sourceCode')}
                </Typography>

                <dl className="grid grid-cols-[max-content_1fr] items-baseline gap-y-3">
                  <dt className="mx-6 py-2">
                    <Typography>{t('contract.verification.title')}</Typography>
                  </dt>
                  <dd className="flex items-center">
                    <VerificationIcon
                      address_eth={account.address_eth!}
                      scope={account}
                      verificationLevel={verificationLevel}
                      hideLink
                    />
                  </dd>

                  {(compilationMetadata?.settings as { compilationTarget?: Record<string, string> })
                    ?.compilationTarget && (
                    <>
                      <dt className="mx-6 py-2">
                        <Typography>{t('contract.name')}</Typography>
                      </dt>
                      <dd>
                        <Typography>
                          {
                            Object.values(
                              (
                                compilationMetadata!.settings as {
                                  compilationTarget: Record<string, string>
                                }
                              ).compilationTarget,
                            )[0]
                          }
                        </Typography>
                      </dd>
                    </>
                  )}

                  {token?.name && (
                    <>
                      <dt className="mx-6 py-2">
                        <Typography>{t('common.tokenName')}</Typography>
                      </dt>
                      <dd>
                        <Typography>{token.name}</Typography>
                      </dd>
                    </>
                  )}
                </dl>
                <Separator className="my-4" />

                {filesSorted?.map(file => (
                  <CodeDisplay key={file.path} code={file.content} label={file.name} language="sol" />
                ))}

                {compilationMetadata && (
                  <CodeDisplay
                    code={JSON.stringify(compilationMetadata, null, 2)}
                    label={t('contract.contractMetadata')}
                    language="sol"
                  />
                )}
              </LinkableDiv>
            )}

            {contract.creation_bytecode && (
              <CodeDisplay
                code={base64ToHex(contract.creation_bytecode)}
                label={t('contract.creationByteCode')}
                language="plaintext"
              />
            )}

            {contract.runtime_bytecode && (
              <CodeDisplay
                code={base64ToHex(contract.runtime_bytecode)}
                label={t('contract.runtimeByteCode')}
                language="plaintext"
              />
            )}
          </LinkableDiv>
        </CardContent>
      )}
    </Card>
  )
}
